/**
 * useCustomerInvoiceAttachments
 *
 * Provides a `fetchAttachments` callback compatible with BulkEmailModal
 * that fetches outstanding invoice transaction IDs for a customer via
 * POST /api/v1/customers/outstanding-invoices, then downloads each
 * invoice document as a base64-encoded attachment.
 *
 * For Dimensions companies, documents are fetched via the ADM Document API
 * instead of the Financial API: for each transaction the first document
 * (limit 1) is retrieved through fetchAdmDocumentsForTransaction and
 * downloaded with downloadAdmDocumentAsBase64.
 */

import { useCallback } from "react";
import { useApi } from "@/hooks/useApi";
import { useTransactionDocumentDownload } from "@/hooks/useTransactionDocumentDownload";
import { useCompanyProductType } from "@/hooks/useCompanyProductType";
import { useAdmDocumentDownloadAsBase64 } from "@/hooks/useAdmDocumentDownloadAsBase64";
import { fetchAdmDocumentsForTransaction } from "@/hooks/useTransactionDocuments";
import { useDocumentApi } from "@/providers/DocumentApiProvider";
import { useAuth } from "@/providers/AuthProvider";
import { useMode } from "@/providers/ModeProvider";
import { toast } from "sonner";
import type { BulkEmailRecipient } from "@/components/shared/bulk-email-modal";
import type { UploadedFile } from "@/lib/email-placeholder-utils";
import type { AdmDocumentContext } from "@/types/adm-document";

interface OutstandingInvoiceSummary {
  transactionId: number;
  invoiceNumber: string;
  outstandingAmount: number;
  externalId?: string;
}

interface CustomerOutstandingInvoices {
  customerId: number;
  customerName: string;
  invoices: OutstandingInvoiceSummary[];
}

interface BatchOutstandingInvoicesResponse {
  customers: CustomerOutstandingInvoices[];
  totalInvoiceCount: number;
}

export function useCustomerInvoiceAttachments() {
  const { apiPost } = useApi();
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const { isDimensions } = useCompanyProductType();
  const { downloadAdmDocumentAsBase64 } = useAdmDocumentDownloadAsBase64();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();
  const { isDemo } = useMode();

  const fetchAttachments = useCallback(
    async (recipient: BulkEmailRecipient): Promise<UploadedFile[]> => {
      if (isDemo) return [];

      const customerId = Number(recipient.id);

      const response = await apiPost<BatchOutstandingInvoicesResponse>(
        "/api/v1/customers/outstanding-invoices",
        { customerIds: [customerId] },
      );

      const customerData = response.customers.find(
        (x) => x.customerId === customerId,
      );
      if (!customerData || customerData.invoices.length === 0) return [];

      if (isDimensions && !getAccessToken) {
        toast.error("Authentication unavailable — cannot fetch invoice documents");
        return [];
      }

      const results = isDimensions
        ? await fetchDimensionsAttachments(
            customerData.invoices,
            getDocumentContext,
            getAccessToken!,
            downloadAdmDocumentAsBase64,
          )
        : await fetchFinancialsAttachments(
            customerData.invoices,
            downloadDocumentAsBase64,
          );

      const successful = results
        .filter(
          (r): r is PromiseFulfilledResult<UploadedFile> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value);

      const failedCount = results.filter(
        (r) => r.status === "rejected",
      ).length;
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} invoice document(s) could not be downloaded`,
        );
      }

      return successful;
    },
    [
      apiPost,
      isDemo,
      isDimensions,
      downloadDocumentAsBase64,
      downloadAdmDocumentAsBase64,
      getDocumentContext,
      getAccessToken,
    ],
  );

  return fetchAttachments;
}

async function fetchFinancialsAttachments(
  invoices: OutstandingInvoiceSummary[],
  downloadDocumentAsBase64: (txId: number) => Promise<{
    base64Content: string;
    fileName: string;
    contentType: string;
  }>,
): Promise<PromiseSettledResult<UploadedFile>[]> {
  return Promise.allSettled(
    invoices.map(async (inv) => {
      const doc = await downloadDocumentAsBase64(inv.transactionId);
      return {
        name: doc.fileName,
        dataUrl: "",
        contentType: doc.contentType,
        base64Content: doc.base64Content,
      } satisfies UploadedFile;
    }),
  );
}

async function fetchDimensionsAttachments(
  invoices: OutstandingInvoiceSummary[],
  getDocumentContext: () => Promise<AdmDocumentContext | null>,
  getAccessToken: () => Promise<string | null>,
  downloadAdmDocumentAsBase64: (documentId: string, fileNameHint?: string, contentTypeHint?: string) => Promise<{
    base64Content: string;
    fileName: string;
    contentType: string;
  }>,
): Promise<PromiseSettledResult<UploadedFile>[]> {
  const ctx = await getDocumentContext();
  if (!ctx) {
    throw new Error("ADM not initialized — could not discover instance");
  }

  return Promise.allSettled(
    invoices.map(async (inv) => {
      if (!inv.externalId) {
        throw new Error(
          `Transaction ${inv.transactionId} has no externalId for ADM lookup`,
        );
      }

      const docs = await fetchAdmDocumentsForTransaction(
        ctx,
        getAccessToken,
        inv.externalId,
        1,
      );

      if (docs.length === 0) {
        throw new Error(
          `No ADM documents found for transaction ${inv.transactionId}`,
        );
      }

      const doc = await downloadAdmDocumentAsBase64(docs[0].id, docs[0].fileName, docs[0].fileType);
      return {
        name: doc.fileName,
        dataUrl: "",
        contentType: doc.contentType,
        base64Content: doc.base64Content,
      } satisfies UploadedFile;
    }),
  );
}
